import { z } from 'zod';

/**
 * Branded 64-bit integer representing micro-units (1 Credit / Unit = 1,000,000 MicroUnits)
 */
export type MicroAmount = bigint & { readonly __brand: unique symbol };

export function toMicroAmount(value: bigint | string | number): MicroAmount {
  const b = typeof value === 'bigint' ? value : BigInt(value);
  if (b < 0n) {
    throw new RangeError(`MicroAmount cannot be negative: ${b}`);
  }
  return b as MicroAmount;
}

/** Validates non-negative integer string on the wire and transforms into branded MicroAmount */
export const microAmountSchema = z
  .string()
  .regex(/^\d+$/,{ message: 'Amount must be an unsigned integer decimal string' })
  .transform((val)=> toMicroAmount(val));

/** Decentralized Identifier (DID) format validator */
export const didSchema = z
  .string()
  .regex(/^did:[a-z0-9]+:[a-zA-Z0-9._:-]+$/,{ message: 'Invalid DID format' });

/** Strict ISO-8601 UTC timestamp validator (must end in Z) */
export const isoUtcDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/, { message: 'Timestamp must be strict ISO-8601 UTC ending in Z' });

/** 64-character lowercase hex string validator (SHA-256) */
export const hexSha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/, { message: 'Must be a 64-character lowercase hex SHA-256 hash' });
